import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Play, Square, Clock, ChevronDown, ChevronUp } from 'lucide-react';

const sounds = {
  clochette: { label: 'Clochette', freq: 880, type: 'sine', decay: 0.6 },
  goutte: { label: 'Goutte d\'eau', freq: 520, type: 'sine', decay: 0.3 },
  bol: { label: 'Bol tibétain', freq: 340, type: 'sine', decay: 1.2 }
};

export const MangeTropViteFree = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('setup');
  const [interval, setIntervalTime] = useState(20);
  const [selectedSound, setSelectedSound] = useState('clochette');
  const [elapsed, setElapsed] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const timerRef = useRef(null);
  const bipTimerRef = useRef(null);
  const audioCtxRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem('meal_timer_history');
    if (stored) {
      try { setHistory(JSON.parse(stored)); } catch (e) {}
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (bipTimerRef.current) clearInterval(bipTimerRef.current);
    };
  }, []);

  const playBip = () => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const s = sounds[selectedSound];
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = s.type;
      osc.frequency.value = s.freq;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + s.decay);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + s.decay);
    } catch (e) {}
  };

  const startGuide = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    setPhase('message');
    setTimeout(() => {
      setPhase('running');
      setElapsed(0);
      timerRef.current = window.setInterval(() => {
        setElapsed(prev => prev + 1);
      }, 1000);
      bipTimerRef.current = window.setInterval(() => {
        playBip();
      }, interval * 1000);
    }, 4000);
  };

  const stopGuide = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (bipTimerRef.current) clearInterval(bipTimerRef.current);
    timerRef.current = null;
    bipTimerRef.current = null;
    setPhase('done');
    const entry = {
      id: Date.now().toString(),
      duration_seconds: elapsed,
      created_at: new Date().toISOString()
    };
    const newHistory = [entry, ...history].slice(0, 30);
    setHistory(newHistory);
    localStorage.setItem('meal_timer_history', JSON.stringify(newHistory));
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatDate = (iso) => {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (phase === 'setup') {
    return (
      <div className="min-h-screen bg-sand-50 pb-24" data-testid="mange-vite-free-setup">
        <div className="bg-gradient-to-b from-sage-100 to-sand-50 px-6 py-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="mb-4">
            <ArrowLeft className="w-5 h-5 text-sage-700" />
          </Button>
          <h1 className="text-3xl font-serif font-light text-sage-700 mb-2">
            Je mange trop vite
          </h1>
          <p className="text-sage-500 text-sm">Guide sonore pour ralentir votre repas</p>
        </div>

        <div className="px-6 py-6">
          <div className="max-w-md mx-auto space-y-6">
            <p className="text-sage-600 leading-relaxed text-sm">
              Lancez ce guide au début de votre repas, puis posez votre téléphone sur la table. Un bip doux vous rappellera régulièrement de poser vos couverts et de savourer.
            </p>

            <div>
              <p className="text-sage-700 font-medium mb-3 text-sm">Intervalle entre chaque rappel :</p>
              <div className="flex gap-3">
                {[15, 20, 30].map(val => (
                  <button
                    key={val}
                    onClick={() => setIntervalTime(val)}
                    className={`flex-1 py-3 rounded-xl border-2 text-center font-medium transition-all text-sm ${
                      interval === val
                        ? 'border-sage-600 bg-sage-600 text-white'
                        : 'border-sage-200 bg-white text-sage-700 hover:border-sage-400'
                    }`}
                  >
                    {val}s
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sage-700 font-medium mb-3 text-sm">Son du rappel :</p>
              <div className="flex gap-3">
                {Object.entries(sounds).map(([key, s]) => (
                  <button
                    key={key}
                    onClick={() => { setSelectedSound(key); playBip(); }}
                    className={`flex-1 py-3 rounded-xl border-2 text-center font-medium transition-all text-xs ${
                      selectedSound === key
                        ? 'border-sage-600 bg-sage-600 text-white'
                        : 'border-sage-200 bg-white text-sage-700 hover:border-sage-400'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={startGuide}
              className="w-full bg-sage-600 hover:bg-sage-600 text-white h-14 rounded-xl text-base"
              data-testid="start-guide-free"
            >
              <Play className="w-5 h-5 mr-2" />
              Lancer le guide
            </Button>

            {history.length > 0 && (
              <div>
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex items-center gap-2 text-sage-500 text-sm w-full justify-center py-2"
                >
                  Mes repas précédents
                  {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showHistory && (
                  <div className="space-y-2 mt-2">
                    {history.map((entry, i) => (
                      <div key={entry.id || i} className="flex items-center justify-between bg-white rounded-lg p-3 border border-sage-100">
                        <span className="text-xs text-sage-500">{formatDate(entry.created_at)}</span>
                        <span className="text-sm font-medium text-sage-700">{formatTime(entry.duration_seconds)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'message') {
    return (
      <div className="min-h-screen bg-sage-500 flex items-center justify-center px-8" data-testid="mange-vite-free-message">
        <div className="text-center">
          <p className="text-white text-xl font-serif leading-relaxed">
            Pensez à poser vos couverts entre chaque bouchée
          </p>
          <p className="text-white/50 text-sm mt-6">Le guide sonore démarre dans quelques secondes...</p>
        </div>
      </div>
    );
  }

  if (phase === 'running') {
    return (
      <div className="min-h-screen bg-sage-500 flex flex-col items-center justify-center px-8" data-testid="mange-vite-free-running">
        <div className="text-center">
          <Clock className="w-12 h-12 text-white/40 mx-auto mb-6" />
          <p className="text-white/60 text-sm mb-2">Durée du repas</p>
          <p className="text-white text-5xl font-serif font-light mb-8">{formatTime(elapsed)}</p>
          <p className="text-white/50 text-sm mb-2">Bip toutes les {interval}s</p>
          <p className="text-white/40 text-xs">Posez le téléphone et savourez</p>
        </div>
        <Button
          onClick={stopGuide}
          className="mt-16 bg-white/10 hover:bg-white/20 text-white h-14 px-10 rounded-xl border border-white/20"
          data-testid="stop-guide-free"
        >
          <Square className="w-5 h-5 mr-2" />
          Fin du repas
        </Button>
      </div>
    );
  }

  const avgDuration = history.length >= 2
    ? Math.round(history.reduce((sum, e) => sum + e.duration_seconds, 0) / history.length)
    : 0;

  return (
    <div className="min-h-screen bg-sand-50 flex flex-col items-center justify-center px-8" data-testid="mange-vite-free-done">
      <div className="text-center max-w-sm">
        <h2 className="text-2xl font-serif text-sage-700 mb-6">Repas terminé</h2>
        <div className="bg-white rounded-2xl p-6 border border-sage-100 mb-4">
          <p className="text-sage-500 text-sm mb-1">Durée de votre repas</p>
          <p className="text-4xl font-serif text-sage-700 mb-4">{formatTime(elapsed)}</p>
          <p className="text-sage-500 text-sm">
            {elapsed >= 1200
              ? 'Excellent ! Vous avez pris votre temps.'
              : elapsed >= 900
              ? 'Très bien ! Continuez à ralentir.'
              : elapsed >= 600
              ? 'Bon début ! Essayez de dépasser 15 minutes la prochaine fois.'
              : 'Essayez de rallonger progressivement la durée de vos repas.'
            }
          </p>
        </div>

        {avgDuration > 0 && history.length >= 2 && (
          <div className="bg-sage-50 rounded-xl p-4 border border-sage-100 mb-4">
            <p className="text-sage-500 text-xs mb-1">Votre moyenne sur {history.length} repas</p>
            <p className="text-lg font-medium text-sage-700">{formatTime(avgDuration)}</p>
          </div>
        )}

        <div className="bg-gradient-to-r from-sage-500 to-sage-600 rounded-xl p-5 text-white mb-4">
          <p className="font-medium text-sm mb-1">Envie d'aller plus loin ?</p>
          <p className="text-white/70 text-xs mb-3">Le programme complet vous accompagne avec des séances d'hypnose pour reprogrammer votre rapport à l'alimentation.</p>
          <Button
            onClick={() => navigate('/')}
            className="bg-declic hover:bg-declic/90 text-white h-9 rounded-lg text-xs px-4"
          >
            Découvrir Déclic RePas
          </Button>
        </div>

        <div className="space-y-3">
          <Button
            onClick={() => { setPhase('setup'); setElapsed(0); }}
            variant="outline"
            className="w-full border-sage-200 text-sage-600 h-11 rounded-xl"
          >
            Recommencer
          </Button>
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="w-full text-sage-400 h-11 rounded-xl"
          >
            Retour à l'accueil
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MangeTropViteFree;
